import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';

/*
 * Style.
 */

const StyledEmptyStateWrapperDiv = styled.div`
  padding: 0 48px 48px 48px;
`;

const StyledEmptyStateBorderDiv = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 20px;
  border: 2px solid ${(p) => p.theme.greys.shade30};
  max-width: 1104px;
  margin: 0 auto;
`;

const StyledInfoWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 310px;
  gap: 24px;
  padding: 48px 24px;
`;

const StyledInfoImg = styled.img<{$width: number; $height: number}>`
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
`;

const StyledInfoTextWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledInfoHeaderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  line-height: 20px;
  max-width: 480px;
  white-space: normal;
  text-align: center;
`;

const StyledInfoBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
  line-height: 21px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportEmptyState: FC = () => {
  const theme = useUserTheme();
  const localize = useLocalize();
  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  return (
    <StyledEmptyStateWrapperDiv>
      <StyledEmptyStateBorderDiv>
        <StyledInfoWrapperDiv>
          <StyledInfoImg
            $width={searchNoResultsAnywhere.width}
            $height={searchNoResultsAnywhere.height}
            src={searchNoResultsAnywhere.src}
            alt={localize(
              defineMessage({
                id: 'analytics-knowledge-base-empty-state-header',
                message: 'No data found',
              }),
            )}
          />
          <StyledInfoTextWrapperDiv>
            <StyledInfoHeaderDiv>
              <Trans id="analytics-metric-knowledge-base-no-activity-header">No activity</Trans>
            </StyledInfoHeaderDiv>
            <StyledInfoBodyDiv>
              <Trans id="analytics-metric-knowledge-base-no-activity-body">
                No data was found for the selected filters. Try adjusting the filters or select a wider date
                range to get results.
              </Trans>
            </StyledInfoBodyDiv>
          </StyledInfoTextWrapperDiv>
        </StyledInfoWrapperDiv>
      </StyledEmptyStateBorderDiv>
    </StyledEmptyStateWrapperDiv>
  );
};
